import { defaultWidgetSizes } from "@/utils/widgets";
import {
  breakpointCols,
  findFirstFitPosition,
  type BreakpointLayouts,
  type LayoutItem,
  type WidgetFilterData,
} from "@/utils/DashboardLayouts";
import type { LayoutExportData } from "@/utils/layoutExportImport";
import type { TemplateWidgetSpec } from "./types";

const LG_COLS = breakpointCols.lg;

type WidgetSize = { w: number; h: number; minW?: number };

function baseIdOf(id: string): string {
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

function sizeFor(spec: TemplateWidgetSpec): WidgetSize {
  const registrySize = (
    defaultWidgetSizes as Record<string, WidgetSize | undefined>
  )[baseIdOf(spec.id)];
  const base = registrySize ?? { w: 4, h: 4, minW: 2 };
  return {
    w: spec.size?.w ?? base.w,
    h: spec.size?.h ?? base.h,
    minW: base.minW,
  };
}

/**
 * Scale an lg-breakpoint width to a narrower breakpoint, clamped to the
 * widget's minW and the breakpoint's column count.
 */
function scaleWidth(lgWidth: number, cols: number, minW = 1): number {
  const scaled = Math.round((lgWidth / LG_COLS) * cols) || 1;
  return Math.max(Math.min(Math.max(scaled, minW), cols), 1);
}

/**
 * Deterministically packs an ordered widget list into layouts for all five
 * breakpoints using the same occupancy-grid first-fit placement the dashboard
 * uses when a user adds a widget (findFirstFitPosition). Used by both starter
 * templates and the concierge apply path — the LLM never emits geometry.
 */
export function packLayouts(specs: TemplateWidgetSpec[]): BreakpointLayouts {
  const layout: BreakpointLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };

  for (const spec of specs) {
    const size = sizeFor(spec);
    for (const bp of ["lg", "md", "sm", "xs", "xxs"] as const) {
      const cols = breakpointCols[bp];
      const w = bp === "lg" ? Math.min(size.w, cols) : scaleWidth(size.w, cols, size.minW);
      const h = size.h;
      const pos = findFirstFitPosition(layout[bp], { w, h }, cols);
      const item: LayoutItem = { i: spec.id, x: pos.x, y: pos.y, w, h };
      layout[bp] = [...layout[bp], item];
    }
  }

  return layout;
}

/**
 * Builds a full LayoutExportData (the same versioned format the export/import
 * pipeline validates) from a tab title and an ordered widget spec list.
 */
export function buildLayoutExportData(
  tabTitle: string,
  specs: TemplateWidgetSpec[]
): LayoutExportData {
  const widgetFilters: Record<string, WidgetFilterData> = {};
  for (const spec of specs) {
    const entry: WidgetFilterData = {};
    if (spec.filter && Object.keys(spec.filter).length > 0) {
      entry.filter = spec.filter;
    }
    if (spec.highlight && Object.keys(spec.highlight).length > 0) {
      entry.highlight = spec.highlight;
    }
    if (entry.filter || entry.highlight) widgetFilters[spec.id] = entry;
  }

  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    tabTitle,
    enabledWidgets: specs.map((s) => s.id),
    layout: packLayouts(specs),
    ...(Object.keys(widgetFilters).length > 0 && { widgetFilters }),
  };
}
